import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Box, TextField, FormControlLabel, Switch, Typography, Tabs, Tab } from '@mui/material';

import { SCUIButton } from '../../../../atomic/atoms';
import { AIGenerationTypeCommand } from '../../../../services/command/AIGenerationTypeCommand';
import {
    AIGenerationType,
    CreateAIGenerationTypeRequest,
    UpdateAIGenerationTypeRequest,
} from '../../../../types/AIGenerationType';
import Swal from 'sweetalert2';

interface AIGenerationTypeDrawerProps {
    open: boolean;
    onClose: () => void;
    generationType: AIGenerationType | null;
    onSaveSuccess: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
    </Box>
);

export const AIGenerationTypeDrawer = ({
    open,
    onClose,
    generationType,
    onSaveSuccess,
}: AIGenerationTypeDrawerProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        iconSvg: '',
        systemPromptCode: '',
        isActive: true,
        displayOrder: 0,
    });
    const [errors, setErrors] = useState<any>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (generationType) {
            setFormData({
                name: generationType.name || '',
                code: generationType.code || '',
                description: generationType.description || '',
                iconSvg: generationType.iconSvg || '',
                systemPromptCode: generationType.systemPromptCode || '',
                isActive: generationType.isActive !== undefined ? generationType.isActive : true,
                displayOrder: generationType.displayOrder || 0,
            });
        } else {
            setFormData({
                name: '',
                code: '',
                description: '',
                iconSvg: '',
                systemPromptCode: '',
                isActive: true,
                displayOrder: 0,
            });
        }
        setErrors({});
        setActiveTab(0);
    }, [generationType, open]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev: any) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors: any = {};

        if (!formData.name.trim()) {
            newErrors.name = t('aiGenerationType.nameRequired');
        }

        if (!formData.code.trim()) {
            newErrors.code = t('aiGenerationType.codeRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username || 'System';

            if (generationType) {
                const request: UpdateAIGenerationTypeRequest = {
                    id: generationType.id,
                    name: formData.name,
                    code: formData.code,
                    description: formData.description,
                    iconSvg: formData.iconSvg,
                    systemPromptCode: formData.systemPromptCode,
                    isActive: formData.isActive,
                    displayOrder: formData.displayOrder,
                    updatedBy: username,
                };
                await AIGenerationTypeCommand.UpdateAIGenerationType(generationType.id, request);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('aiGenerationType.updateSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                const request: CreateAIGenerationTypeRequest = {
                    name: formData.name,
                    code: formData.code,
                    description: formData.description,
                    iconSvg: formData.iconSvg,
                    systemPromptCode: formData.systemPromptCode,
                    isActive: formData.isActive,
                    displayOrder: formData.displayOrder,
                    createdBy: username,
                };
                await AIGenerationTypeCommand.CreateAIGenerationType(request);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('aiGenerationType.addSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
            onSaveSuccess();
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: generationType ? t('aiGenerationType.updateError') : t('aiGenerationType.addError'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 600, padding: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {generationType ? t('aiGenerationType.edit') : t('aiGenerationType.add')}
                </Typography>

                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={t('aiGenerationType.tabs.general')} />
                    <Tab label={t('aiGenerationType.tabs.appearance')} />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label={t('aiGenerationType.name')}
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            placeholder={t('aiGenerationType.placeholders.name')}
                            required
                            fullWidth
                        />

                        <TextField
                            label={t('aiGenerationType.code')}
                            value={formData.code}
                            onChange={(e) => handleChange('code', e.target.value)}
                            error={!!errors.code}
                            helperText={errors.code || t('aiGenerationType.codeHelperText')}
                            placeholder={t('aiGenerationType.placeholders.code')}
                            required
                            fullWidth
                            disabled={!!generationType}
                        />

                        <TextField
                            label={t('aiGenerationType.description')}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={t('aiGenerationType.placeholders.description')}
                            fullWidth
                            multiline
                            rows={3}
                        />

                        <TextField
                            label={t('aiGenerationType.displayOrder')}
                            type="number"
                            value={formData.displayOrder}
                            onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                            placeholder={t('aiGenerationType.placeholders.displayOrder')}
                            fullWidth
                        />

                        <TextField
                            label={t('aiGenerationType.systemPromptCode')}
                            value={formData.systemPromptCode}
                            onChange={(e) => handleChange('systemPromptCode', e.target.value)}
                            placeholder={t('aiGenerationType.placeholders.systemPromptCode')}
                            helperText={t('aiGenerationType.systemPromptCodeHelperText')}
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                />
                            }
                            label={t('aiGenerationType.isActive')}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label={t('aiGenerationType.iconSvg')}
                            value={formData.iconSvg}
                            onChange={(e) => handleChange('iconSvg', e.target.value)}
                            placeholder={t('aiGenerationType.placeholders.iconSvg')}
                            fullWidth
                            multiline
                            rows={8}
                            helperText={t('aiGenerationType.iconSvgHelperText')}
                        />

                        {formData.iconSvg && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    {t('aiGenerationType.iconPreview')}
                                </Typography>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 1,
                                        mt: 0.5,
                                        '& svg': { width: '100%', height: '100%' },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: formData.iconSvg }}
                                />
                            </Box>
                        )}
                    </Box>
                </TabPanel>

                <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
                    <SCUIButton
                        sizeType="fixed"
                        text={t('general.save')}
                        type="primary"
                        size="m"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        fullwidth
                    />
                    <SCUIButton
                        sizeType="fixed"
                        text={t('general.cancel')}
                        type="secondary"
                        size="m"
                        onClick={onClose}
                        disabled={isSubmitting}
                        fullwidth
                    />
                </Box>
            </Box>
        </Drawer>
    );
};
